import React from "react";
import { Link } from "react-router-dom";
import { isPlayStoreApp } from "../lib/appMode";
import LiveTournamentHomeBanner from "../components/LiveTournamentHomeBanner";

/**
 * GA RED – HOME PAGE
 * Non-destructive visual upgrade
 * All logic & structure preserved
 */
const Home = () => {
  const playStoreApp = isPlayStoreApp();
  const iosAppUrl = "https://apps.apple.com/us/app/gastreaming/id6796517516";

  const wrap = {
    minHeight: "calc(100vh - 90px)",
    padding: "48px 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",

    /* Route-level video background is supplied by App.jsx. */
    backgroundImage: "none",
    position: "relative",
  };

  const overlay = {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at center, rgba(0,0,0,0.55), rgba(0,0,0,0.88))",
    zIndex: 0,
  };

  const card = {
    position: "relative",
    zIndex: 1,

    width: "min(1100px, 100%)",
    borderRadius: "22px",
    padding: "32px",
    background: "rgba(8,8,8,0.72)",
    border: "1px solid rgba(255,0,0,0.35)",
    boxShadow:
      "0 20px 60px rgba(0,0,0,0.6), 0 0 25px rgba(255,0,0,0.15)",
    backdropFilter: "blur(14px)",
  };

  const titleRow = {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: "10px",
  };

  const badge = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 999,
    background: "rgba(255,0,0,0.14)",
    border: "1px solid rgba(255,0,0,0.45)",
    color: "rgba(255,255,255,0.95)",
    fontSize: 13,
    letterSpacing: 0.4,
    boxShadow: "0 0 12px rgba(255,0,0,0.35)",
  };

  const h1 = {
    margin: 0,
    fontSize: "clamp(32px, 4.2vw, 46px)",
    lineHeight: 1.05,
    color: "#ffffff",
    textShadow: "0 0 20px rgba(255,0,0,0.25)",
  };

  const sub = {
    marginTop: 14,
    marginBottom: 26,
    fontSize: "clamp(15px, 1.6vw, 18px)",
    lineHeight: 1.6,
    color: "rgba(255,255,255,0.82)",
    maxWidth: 820,
  };

  const btnRow = {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 10,
    marginBottom: 30,
  };

  const btn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "13px 18px",
    borderRadius: 14,
    textDecoration: "none",
    fontWeight: 700,
    letterSpacing: 0.3,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    transition: "all 0.2s ease",
  };

  const btnPrimary = {
    ...btn,
    background: "linear-gradient(135deg, #ff1a1a, #c80000)",
    border: "1px solid rgba(255,0,0,0.75)",
    color: "#fff",
    boxShadow: "0 0 18px rgba(255,0,0,0.45)",
  };

  const btnIOS = {
    ...btn,
    background: "linear-gradient(135deg, #171717, #000000)",
    border: "1px solid rgba(255,255,255,0.34)",
    color: "#fff",
    boxShadow: "0 0 18px rgba(255,255,255,0.08)",
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
    marginTop: 22,
  };

  const tile = {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.55)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "rgba(255,255,255,0.9)",
    boxShadow: "0 8px 28px rgba(0,0,0,0.55)",
  };

  const tileLink = {
    ...tile,
    textDecoration: "none",
    color: "inherit",
    display: "block",
    cursor: "pointer",
  };

  const tileTitle = {
    margin: "0 0 6px 0",
    fontSize: 17,
    color: "#ffffff",
  };

  const tileText = {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.78)",
  };

  const hr = {
    margin: "22px 0 16px",
    border: "none",
    height: 1,
    background: "rgba(255,255,255,0.12)",
  };

  return (
    <div style={wrap}>
      <div style={overlay} />

      <section style={card}>
        <div style={titleRow}>
          <div style={badge}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "#ff1a1a",
                boxShadow: "0 0 8px rgba(255,0,0,0.8)",
              }}
            />
            GAStreaming.live • Official Hub
          </div>

          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
            Good vibes • Real community • Big energy
          </div>
        </div>

        <h1 style={h1} className="metallic-title">Welcome to GAStreaming</h1>

        <p style={sub}>
          Where the squad pulls up for <b>live streams</b>, <b>clips</b>, and{" "}
          <b>real competition</b>. No weird energy — just laughs, growth, and that
          “run it back” mentality. Tap in, check the featured streamers, and{" "}
          <b>Get Active</b>.
        </p>

        <LiveTournamentHomeBanner />

        <div style={btnRow}>
          <Link to="/streams" style={btnPrimary} className="premium-btn">🔥 Watch Live Channels</Link>
          {!playStoreApp ? (
            <a href={iosAppUrl} target="_blank" rel="noopener noreferrer" style={btnIOS}>
               Download iPhone App
            </a>
          ) : null}
          <Link to="/games" style={btn}>♠ GA Games</Link>
          <Link to="/register" style={btn}>🧾 Create Account</Link>
          <Link to="/man-cave" style={btn}>🏆 The Man Cave</Link>
          <Link to="/feed" style={btn}>📰 Community Feed</Link>
        </div>

        <div style={hr} />

        <div style={grid}>
          <div style={tile}>
            <h3 style={tileTitle}>🎮 Post Your Gameplay</h3>
            <p style={tileText}>
              Build your profile, drop clips, and let the community see what you
              on.
            </p>
          </div>

          <div style={tile}>
            <h3 style={tileTitle}>🤝 Community First</h3>
            <p style={tileText}>
              This is a real spot — support the creators, talk your talk, and
              keep it respectful. We’re building something long-term.
            </p>
          </div>

          <div style={tile}>
            <h3 style={tileTitle}>🚀 Always Improving</h3>
            <p style={tileText}>
              {playStoreApp
                ? "New features keep landing: tournaments, upgrades, and more. Stay close — you don’t want to miss the wave."
                : "New features keep landing: tournaments, wagers, upgrades, and more. Stay close — you don’t want to miss the wave."}
            </p>
          </div>

          <Link to="/subscribe" style={tileLink}>
            <div>
              <h3 style={tileTitle}>💚 Become a Member</h3>
              <p style={tileText}>
                Members get the full experience: profiles, chat access, perks,
                and future unlocks. If you’re here, you’re early.
              </p>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
